import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import {
  type GetPaymentInfoResponse,
  SubscriptionInterval,
} from '@jotta/grpc-connect-openapi/payment'
import { useLocale } from '@jotta/i18n'
import { StripeElementsContainer } from '@jotta/settings/StripeContainer'
import { SubscriptionExpirationInfo } from '@jotta/settings/SubscriptionExpirationInfo'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import { ChangePaymentMethodDialog } from '@jotta/ui/ChangePaymentMethodDialog'
import SvgCreditCard from '@jotta/ui/SvgCreditCard'
import SvgListDashes from '@jotta/ui/SvgListDashes'
import { showToast } from '@jotta/ui/ToastProvider'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import type { Money } from '@jotta/utils/formatPrice'
import { formatPriceConnect } from '@jotta/utils/formatPrice'

import { BizWidget } from '../widgets/BizWidget'

type dialogState = 'closed' | 'changePayment'

export function BizPaymentInformation({
  paymentInfo,
}: {
  paymentInfo: GetPaymentInfoResponse
}) {
  const locale = useLocale()
  const [dialogWindowState, setDialogWindowState] =
    useState<dialogState>('closed')
  const closeDialogWindow = () => setDialogWindowState('closed')

  const paymentMethod = paymentInfo?.paymentInfo?.paymentMethod
  const canChangePaymentMethod = (() => {
    switch (paymentMethod?.case) {
      case 'card':
      case 'invoiceBilling':
      case 'externalBilling':
      case 'paypal':
      case 'vippsBilling':
      case 'waivedBilling':
        return true
      case 'noPayment':
      case 'appStoreBilling':
      case 'playStoreBilling':
      case undefined:
        return false
      default:
        return exhaustiveGuard(paymentMethod)
    }
  })()
  const expirationDateMillis = paymentInfo?.expirationDateMillis
  const paymentMethodFormatted = useMemo(() => {
    switch (paymentMethod?.case) {
      case undefined:
        return undefined
      case 'noPayment':
        return expirationDateMillis
          ? undefined
          : t`Free subscription - no payment`
      case 'card':
        return t`Card`
      case 'vippsBilling':
        return 'Vipps'
      case 'paypal':
        return 'PayPal'
      case 'appStoreBilling':
        return 'App Store'
      case 'playStoreBilling':
        return 'Google Play Store'
      case 'invoiceBilling':
        return t`Invoice`
      case 'externalBilling':
        return t`External billing`
      case 'waivedBilling':
        return t`Waived`
      default:
        return exhaustiveGuard(paymentMethod)
    }
  }, [paymentMethod, expirationDateMillis])

  return (
    <BizWidget title={t`Billing`}>
      <SubscriptionExpirationInfo subscription={paymentInfo} />
      <dl className="dl pt-2">
        {paymentMethodFormatted && (
          <>
            <dt>
              <Trans>Payment type</Trans>
            </dt>
            <dd>{paymentMethodFormatted}</dd>
          </>
        )}
        {paymentMethod?.case === 'card' ? (
          <>
            <dt>
              <Trans>Credit Card</Trans>
            </dt>
            <dd>{paymentMethod.value.cardMask}</dd>
          </>
        ) : null}
        {!!paymentInfo.credit && (
          <>
            <dt>
              <Trans>Credit</Trans>
            </dt>
            <dd>{formatPriceConnect(paymentInfo.credit, locale)}</dd>
          </>
        )}
        {!!paymentInfo.discountPercentage && (
          <>
            <dt>
              <Trans>Discount</Trans>
            </dt>
            <dd>{paymentInfo.discountPercentage}%</dd>
          </>
        )}
        {!!paymentInfo.price && (
          <>
            <dt>
              <Trans>Payment Amount</Trans>
            </dt>
            <dd>
              <DisplaySubscriptionPrice
                subscriptionInterval={paymentInfo.subscriptionInterval}
                price={paymentInfo.price}
              />
            </dd>
          </>
        )}
        {!!paymentInfo.renewalDateMillis && (
          <>
            <dt>
              <Trans>Next payment</Trans>
            </dt>
            <dd>{dayjs(paymentInfo.renewalDateMillis.value).format('L')}</dd>
          </>
        )}
      </dl>
      <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2">
        {canChangePaymentMethod && (
          <Button
            className="btn btn-outline"
            onClick={() => setDialogWindowState('changePayment')}
            icon={SvgCreditCard}
          >
            <Trans>Change payment method</Trans>
          </Button>
        )}
        <Link to="billingHistory" className="btn btn-outline">
          <PlainBrandIcon icon={SvgListDashes} />
          <Trans>Billing history</Trans>
        </Link>
      </div>
      <StripeElementsContainer>
        <ChangePaymentMethodDialog
          isOpen={dialogWindowState === 'changePayment'}
          onOpen={() => setDialogWindowState('changePayment')}
          onClose={closeDialogWindow}
          onSuccess={async () => {
            showToast({
              message: t`Payment method updated`,
              hasCloseButton: true,
              duration: 2000,
            })
          }}
        />
      </StripeElementsContainer>
    </BizWidget>
  )
}

export function DisplaySubscriptionPrice({
  price,
  subscriptionInterval,
}: {
  price: Money
  subscriptionInterval: SubscriptionInterval
}) {
  const locale = useLocale()
  if (!price) {
    return null
  }
  const priceString = formatPriceConnect(price, locale)
  let intervalString = ''
  switch (subscriptionInterval) {
    case SubscriptionInterval.NOT_SET:
      return priceString
    case SubscriptionInterval.MONTHLY:
      intervalString = t`per month`
      break
    case SubscriptionInterval.YEARLY:
      intervalString = t`per year`
      break
    case SubscriptionInterval.FIXED_1MND:
      intervalString = t`per month`
      break
    case SubscriptionInterval.FIXED_2MND:
      intervalString = t`for 2 months`
      break
    case SubscriptionInterval.FIXED_3MND:
      intervalString = t`for 3 months`
      break
    case SubscriptionInterval.FIXED_6MND:
      intervalString = t`for 6 months`
      break
    case SubscriptionInterval.FIXED_12MND:
      intervalString = t`for 12 months`
      break
    case SubscriptionInterval.FIXED_24MND:
      intervalString = t`for 24 months`
      break
    case SubscriptionInterval.FIXED_36MND:
      intervalString = t`for 36 months`
      break
    case SubscriptionInterval.FIXED_60MND:
      intervalString = t`for 5 years`
      break
  }
  return `${priceString} ${intervalString}`
}
